import React, { useEffect, useState, useRef } from "react";
import styles from "./Shop.module.css";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import { useNavigate, useLocation } from "react-router-dom";
import NewArrivalsStories from "./NewArrivalsStories";
import OnSaleBanner from "./OnSaleBanner";

interface Category {
  category_id: number;
  category_name: string;
  image?: string;
  type_category: string;
  has_child_category: boolean;
}

const NAVBAR_OFFSET = 64;
const SHOP_STATE_KEY = "shopState:v1";

function scrollToWithOffset(el: HTMLElement, offset = NAVBAR_OFFSET) {
  const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({ top: y, behavior: "smooth" });
}

const Shop: React.FC = () => {
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<Category | null>(null);
  const [subCategoryPath, setSubCategoryPath] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [mainLoading, setMainLoading] = useState(false);
  const [mainError, setMainError] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const customer_id = (location.state as any)?.customer_id || null;
  const subcategoriesRef = useRef<HTMLDivElement>(null);

  // to ensure we run restore only once per mount
  const [didRestore, setDidRestore] = useState(false);

  // Load main categories
  useEffect(() => {
    setMainLoading(true);
    setMainError("");
    WebService.getAPI<any>({ action: `/v1/door-management/dashboard/categories` })
      .then((res) => {
        let data: Category[] = [];
        if (Array.isArray(res.data)) data = res.data;
        else if (Array.isArray(res.data?.data)) data = res.data.data;
        else if (Array.isArray(res)) data = res;
        setMainCategories(
          data.map((c) => ({
            ...c,
            type_category: (c.type_category || "PRODUCT").toUpperCase(),
          }))
        );
      })
      .catch(() => setMainError("Could not load categories. Please refresh or try later."))
      .finally(() => setMainLoading(false));
  }, []);

  // Restore state (selected main, path, scroll)
  useEffect(() => {
    if (didRestore || mainLoading || mainCategories.length === 0) return;

    const raw = sessionStorage.getItem(SHOP_STATE_KEY);
    if (!raw) {
      setDidRestore(true);
      return;
    }

    try {
      const saved = JSON.parse(raw);
      if (saved?.main) {
        const restoredMain: Category =
          mainCategories.find(
            (c) =>
              c.category_id === saved.main.category_id &&
              (c.type_category || "").toUpperCase() ===
                (saved.main.type_category || "PRODUCT").toUpperCase()
          ) || {
            ...saved.main,
            type_category: (saved.main.type_category || "PRODUCT").toUpperCase(),
          };

        setSelectedMainCategory(restoredMain);
        setSubCategoryPath(Array.isArray(saved.path) ? saved.path : []);

        // restore scroll AFTER React applies state
        requestAnimationFrame(() => {
          const scrollY = Number(saved.scrollY) || 0;
          window.scrollTo(0, scrollY);
        });
      }
    } catch {
      // ignore malformed storage
    } finally {
      setDidRestore(true);
    }
  }, [mainCategories, mainLoading, didRestore]);

  // Persist state (except scroll, handled in onScroll below)
  useEffect(() => {
    const state = {
      main: selectedMainCategory,
      path: subCategoryPath,
      scrollY: window.scrollY,
    };
    sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify(state));
  }, [selectedMainCategory, subCategoryPath]);

  // Persist scroll position
  useEffect(() => {
    const onScroll = () => {
      const raw = sessionStorage.getItem(SHOP_STATE_KEY);
      const prev = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(
        SHOP_STATE_KEY,
        JSON.stringify({ ...prev, scrollY: window.scrollY })
      );
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Load subcategories when selection/path changes
  useEffect(() => {
    if (!selectedMainCategory) return;
    setSubLoading(true);
    setSubError("");
    setSubcategories([]);

    const last =
      subCategoryPath.length > 0
        ? subCategoryPath[subCategoryPath.length - 1]
        : selectedMainCategory;

    if (last.has_child_category === false) {
      setSubcategories([]);
      setSubLoading(false);
      return;
    }

    WebService.getAPI<any>({
      action: `/v1/door-management/dashboard/type/${last.type_category}/categories/${last.category_id}/subcategories`,
    })
      .then((res) => {
        let data: Category[] = [];
        if (Array.isArray(res.data)) data = res.data;
        else if (Array.isArray(res.data?.data)) data = res.data.data;
        else if (Array.isArray(res)) data = res;
        setSubcategories(
          data.map((c) => ({
            ...c,
            type_category: (
              c.type_category ||
              last.type_category ||
              "PRODUCT"
            ).toUpperCase(),
          }))
        );

        // Scroll to subcategories section after loading (with navbar-safe offset)
        if (data.length > 0) {
          requestAnimationFrame(() => {
            if (subcategoriesRef.current) {
              scrollToWithOffset(subcategoriesRef.current);
            }
          });
        }
      })
      .catch(() => setSubError("Could not load subcategories."))
      .finally(() => setSubLoading(false));
  }, [selectedMainCategory, subCategoryPath]);

  // ❌ REMOVE the auto-navigate effect that pushed users to /watchlist on leaf.
  // (That effect caused unwanted navigation immediately after restoring state.)

  // Click handlers: navigate to watchlist ONLY on explicit click when the node is a leaf
  const handleMainCategoryClick = (cat: Category) => {
    // save state that includes this click (for when we come back)
    const nextPath: Category[] = [];
    const nextState = {
      main: cat,
      path: nextPath,
      scrollY: window.scrollY,
    };
    sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));

    if (cat.has_child_category === false) {
      navigate(
        `/watchlist/${cat.type_category}/${cat.category_id}`,
        customer_id ? { state: { customer_id } } : undefined
      );
      return;
    }

    setSelectedMainCategory(cat);
    setSubCategoryPath([]);
    setSubError("");
  };

  const handleSubcategoryClick = (cat: Category) => {
    const nextPath = [...subCategoryPath, cat];

    // save state that includes this click (for when we come back)
    const nextState = {
      main: selectedMainCategory,
      path: nextPath,
      scrollY: window.scrollY,
    };
    sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));

    if (cat.has_child_category === false) {
      navigate(
        `/watchlist/${cat.type_category}/${cat.category_id}`,
        customer_id ? { state: { customer_id } } : undefined
      );
      return;
    }

    setSubCategoryPath(nextPath);
    setSubError("");
  };

  const handleBreadcrumbClick = (idx: number) => {
    const sliced = subCategoryPath.slice(0, idx + 1);
    setSubCategoryPath(sliced);
    setSubError("");

    const nextState = {
      main: selectedMainCategory,
      path: sliced,
      scrollY: window.scrollY,
    };
    sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));
  };

  const lastSelected =
    subCategoryPath.length > 0
      ? subCategoryPath[subCategoryPath.length - 1]
      : selectedMainCategory;
  const atLeaf = !!(lastSelected && lastSelected.has_child_category === false);

  return (
    <div className={styles.shopPage}>
      {/* -- Trending (best sellers, refreshed monthly) -- */}
      <div style={{ marginTop: "16px", marginBottom: "0px" }}>
        <OnSaleBanner />
      </div>

      {/* -- New Arrivals Stories -- */}
      <div style={{ marginTop: "-10px", marginBottom: "28px" }}>
        <NewArrivalsStories />
      </div>

      {/* -- Divider for visual separation -- */}
      <div
        style={{
          width: "100%",
          height: "1px",
          background: "linear-gradient(90deg,#ffd40017 0%,#fff 50%,#ffd40017 100%)",
          margin: "0 0 32px 0",
        }}
      />

      {/* -- Categories Grid with heading -- */}
      <div className={styles.categoriesSection}>
        <div>
          <div className={styles.categoriesBigTitle}>
            <span className={styles.catIcon}>🚪</span>{" "}
            Categories{" "}
            <span className={styles.catIcon}>🚪</span>
          </div>
          <div className={styles.categoriesBigTitleUnderline}></div>
        </div>

        {mainLoading ? (
          <div style={{ padding: "22px", color: "#FFD400", fontWeight: 600 }}>
            Loading categories...
          </div>
        ) : mainError ? (
          <div style={{ padding: "22px", color: "#E13D22", fontWeight: 600 }}>
            {mainError}
            <button
              style={{
                background: "#FFD400",
                color: "#232323",
                border: "none",
                marginLeft: 12,
                borderRadius: 6,
                fontWeight: 600,
                cursor: "pointer",
                padding: "5px 16px",
              }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className={styles.categoriesGrid}>
            {mainCategories.map((cat) => (
              <div
                key={`${cat.type_category}-${cat.category_id}`}
                className={`
                  ${styles.categoryGridCard}
                  ${selectedMainCategory?.category_id === cat.category_id &&
                    selectedMainCategory?.type_category === cat.type_category
                    ? styles.selectedGridCategory
                    : ""}
                `}
                onClick={() => handleMainCategoryClick(cat)}
              >
                <div className={styles.categoryImageContainer}>
                  <img
                    src={cat.image && cat.image.length > 0 ? mediaUrl(cat.image) : "/Assets/no-pictures.png"}
                    alt={cat.category_name}
                    className={styles.categoryGridImg}
                    loading="lazy"
                    draggable={false}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                    }}
                  />
                </div>
                <div className={styles.categoryGridName}>{cat.category_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* -- Breadcrumb -- */}
      {subCategoryPath.length > 0 && (
        <div className={styles.breadcrumbRow}>
          <span className={styles.breadcrumbRoot}>
            <button
              className={styles.breadcrumbBtn}
              onClick={() => {
                setSubCategoryPath([]);
                const nextState = {
                  main: selectedMainCategory,
                  path: [],
                  scrollY: window.scrollY,
                };
                sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));
              }}
            >
              {selectedMainCategory?.category_name}
            </button>
            {subCategoryPath.length > 0 && <span> &rarr; </span>}
          </span>
          {subCategoryPath.map((cat, idx) => (
            <span key={cat.category_id}>
              <button
                className={styles.breadcrumbBtn}
                onClick={() => handleBreadcrumbClick(idx)}
              >
                {cat.category_name}
              </button>
              {idx < subCategoryPath.length - 1 && <span> &rarr; </span>}
            </span>
          ))}
        </div>
      )}

      {/* -- Subcategories Grid -- */}
      {/* scrollMarginTop ensures native scrollIntoView won't hide the heading under navbar */}
      <div ref={subcategoriesRef} style={{ scrollMarginTop: NAVBAR_OFFSET }}>
        {(subLoading && !atLeaf) ? (
          <div style={{ padding: "22px", color: "#FFD400", fontWeight: 600 }}>
            Loading subcategories...
          </div>
        ) : subError && !atLeaf ? (
          <div style={{ color: "#e43e19", padding: 22, fontWeight: 600 }}>
            {subError}
          </div>
        ) : subcategories.length > 0 && !atLeaf ? (
          <div className={styles.categoriesSection}>
            <div className={styles.categoriesBigTitle + " " + styles.subcategoriesBigTitle}>
              Subcategories - {lastSelected?.category_name}
            </div>
            <div className={styles.categoriesBigTitleUnderline}></div>

            <div className={styles.categoriesGrid}>
              {subcategories.map((cat) => (
                <div
                  key={`${cat.type_category}-${cat.category_id}`}
                  className={`
                    ${styles.categoryGridCard}
                    ${subCategoryPath[subCategoryPath.length - 1]?.category_id === cat.category_id &&
                      subCategoryPath[subCategoryPath.length - 1]?.type_category === cat.type_category
                      ? styles.selectedGridCategory
                      : ""}
                  `}
                  onClick={() => handleSubcategoryClick(cat)}
                >
                  <div className={styles.categoryImageContainer}>
                    <img
                      src={cat.image && cat.image.length > 0 ? mediaUrl(cat.image) : "/Assets/no-pictures.png"}
                      alt={cat.category_name}
                      className={styles.categoryGridImg}
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                      }}
                    />
                  </div>
                  <div className={styles.categoryGridName}>{cat.category_name}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* -- Bottom Logo Banner (unchanged) -- */}
      <div
        style={{
          width: "100vw",
          maxWidth: "100%",
          margin: "64px 0 0 0",
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          background: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 0,
          minHeight: 140,
        }}
      >
        {/* <img
          src="/Assets/Frame.png"
          alt="sale_baner"
          style={{ width: "100%", maxWidth: 1100, borderRadius: 15, objectFit: "cover" }}
          draggable={false}
          loading="eager"
        /> */}
      </div>
    </div>
  );
};

export default Shop;